import React, { useEffect, useState } from 'react'
import { Award, Clock, Edit, MessageSquare, Save, Star, User, X } from 'lucide-react'

import { apiClient } from '@/lib/api'

interface EvaluationDisplayProps {
  sourceTable: 'code_submissions' | 'interviews_responses' | 'system_design_responses' | 'whiteboard_responses' | 'uml_design_responses' | 'data_modeling_responses'
  sourceRecordUuid: string
  candidateUuid: string
  interviewUuid?: string
  title: string
  onEvaluationChange?: () => void
}

interface Evaluation {
  uuid: string
  evaluator_feedback?: string
  evaluator_marks?: number
  evaluated_at?: string
  evaluated_by?: number
  evaluator_uuid?: string
  evaluator_first_name?: string
  evaluator_last_name?: string
  evaluator_email?: string
}

const getScoreColor = (score: number) => {
  if (score >= 90) return 'text-green-600 bg-green-100'
  if (score >= 80) return 'text-blue-600 bg-blue-100'
  if (score >= 70) return 'text-yellow-600 bg-yellow-100'
  return 'text-red-600 bg-red-100'
}

export default function EvaluationDisplay({
  sourceTable,
  sourceRecordUuid,
  candidateUuid,
  interviewUuid,
  title,
  onEvaluationChange
}: EvaluationDisplayProps) {
  const [evaluations, setEvaluations] = useState<Evaluation[]>([])
  const [myEvaluation, setMyEvaluation] = useState<Evaluation | null>(null)
  const [otherEvaluations, setOtherEvaluations] = useState<Evaluation[]>([])
  const [currentUserUuid, setCurrentUserUuid] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editFeedback, setEditFeedback] = useState('')
  const [editMarks, setEditMarks] = useState<number | ''>('')
  const [newFeedback, setNewFeedback] = useState('')
  const [newMarks, setNewMarks] = useState<number | ''>('')

  useEffect(() => {
    loadCurrentUser()
  }, [])

  useEffect(() => {
    loadEvaluations()
  }, [sourceTable, sourceRecordUuid, currentUserUuid])

  const loadCurrentUser = async () => {
    try {
      const response = await apiClient.getCurrentUser()
      if (response.success && response.data) {
        const user = response.data
        const uuidCandidate = user.uuid || user.user_uuid || user.id
        if (typeof uuidCandidate === 'string' && uuidCandidate.length > 0) {
          setCurrentUserUuid(uuidCandidate)
        }
      }
    } catch (err) {
      console.error('Failed to load current user', err)
    }
  }

  const loadEvaluations = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getEvaluation(sourceTable, sourceRecordUuid)
      const data: Evaluation[] = response.success
        ? (Array.isArray(response.data) ? response.data : response.data ? [response.data as Evaluation] : [])
        : []

      setEvaluations(data)

      if (currentUserUuid) {
        const mine = data.find((item) => item.evaluator_uuid === currentUserUuid)
        setMyEvaluation(mine || null)
        setOtherEvaluations(data.filter((item) => item.evaluator_uuid !== currentUserUuid))
        if (mine && !isEditing) {
          setEditFeedback(mine.evaluator_feedback || '')
          setEditMarks(mine.evaluator_marks ?? '')
        }
      } else {
        setMyEvaluation(null)
        setOtherEvaluations(data)
      }
    } catch (err) {
      console.error('Failed to load evaluations:', err)
      setEvaluations([])
      setMyEvaluation(null)
      setOtherEvaluations([])
    } finally {
      setLoading(false)
    }
  }

  const handleSaveEdit = async () => {
    if (!myEvaluation) return
    try {
      setSaving(true)
      const response = await apiClient.updateEvaluation(myEvaluation.uuid, {
        evaluator_feedback: editFeedback.trim() || undefined,
        evaluator_marks: editMarks !== '' ? Number(editMarks) : undefined
      })

      if (response.success) {
        await loadEvaluations()
        setIsEditing(false)
        onEvaluationChange?.()
      }
    } catch (err) {
      console.error('Failed to update evaluation:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleCreateEvaluation = async () => {
    try {
      setSaving(true)
      const response = await apiClient.createEvaluation({
        source_table: sourceTable,
        source_record_uuid: sourceRecordUuid,
        candidate_uuid: candidateUuid,
        interview_uuid: interviewUuid,
        evaluator_feedback: newFeedback.trim() || undefined,
        evaluator_marks: newMarks !== '' ? Number(newMarks) : undefined
      })

      if (response.success) {
        await loadEvaluations()
        setShowForm(false)
        setNewFeedback('')
        setNewMarks('')
        onEvaluationChange?.()
      }
    } catch (err) {
      console.error('Failed to create evaluation:', err)
    } finally {
      setSaving(false)
    }
  }

  const canEdit = myEvaluation !== null && currentUserUuid !== null && myEvaluation.evaluator_uuid === currentUserUuid

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Award className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-semibold text-gray-800">Evaluation: {title}</span>
        </div>
        {myEvaluation?.evaluated_at && (
          <span className="text-xs text-gray-500">Updated {new Date(myEvaluation.evaluated_at).toLocaleString()}</span>
        )}
      </div>

      {loading && (
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
          <div className="h-12 bg-gray-200 rounded" />
        </div>
      )}

      {!loading && myEvaluation && !isEditing && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {myEvaluation.evaluator_first_name} {myEvaluation.evaluator_last_name}
                </p>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  <span>{myEvaluation.evaluated_at ? new Date(myEvaluation.evaluated_at).toLocaleString() : 'Not yet evaluated'}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {myEvaluation.evaluator_marks !== undefined && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(myEvaluation.evaluator_marks)}`}>
                  {myEvaluation.evaluator_marks}/100
                </span>
              )}
              {canEdit && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Edit evaluation"
                >
                  <Edit className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {myEvaluation.evaluator_feedback || 'No comments provided.'}
          </p>
        </div>
      )}

      {!loading && myEvaluation && isEditing && (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-medium text-blue-900">Edit Evaluation</h5>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-3 h-3" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="flex items-center space-x-1 px-3 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
              >
                <X className="w-3 h-3" />
                <span>Cancel</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Marks (0-100)</label>
            <input
              type="number"
              min="0"
              max="100"
              value={editMarks}
              onChange={(e) => setEditMarks(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Feedback</label>
            <textarea
              value={editFeedback}
              onChange={(e) => setEditFeedback(e.target.value)}
              rows={3}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              placeholder="Enter your evaluation feedback..."
            />
          </div>
        </div>
      )}

      {!loading && !myEvaluation && !showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <MessageSquare className="w-4 h-4" />
          <span>Add Evaluation</span>
        </button>
      )}

      {!loading && !myEvaluation && showForm && (
        <div className="bg-green-50 rounded-lg p-4 border border-green-200 space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-medium text-green-900">Add Evaluation</h5>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCreateEvaluation}
                disabled={saving || (!newFeedback.trim() && newMarks === '')}
                className="flex items-center space-x-1 px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="w-3 h-3" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
              <button
                onClick={() => {
                  setShowForm(false)
                  setNewFeedback('')
                  setNewMarks('')
                }}
                className="flex items-center space-x-1 px-3 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
              >
                <X className="w-3 h-3" />
                <span>Cancel</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Marks (0-100)</label>
            <input
              type="number"
              min="0"
              max="100"
              value={newMarks}
              onChange={(e) => setNewMarks(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Feedback</label>
            <textarea
              value={newFeedback}
              onChange={(e) => setNewFeedback(e.target.value)}
              rows={3}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
              placeholder="Enter your evaluation feedback..."
            />
          </div>
        </div>
      )}

      {!loading && otherEvaluations.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700">Other Evaluations</h4>
          {otherEvaluations.map((ev) => {
            const name = [ev.evaluator_first_name, ev.evaluator_last_name].filter(Boolean).join(' ') || ev.evaluator_email || 'Evaluator'
            return (
              <div key={ev.uuid} className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <User className="w-4 h-4" />
                    <span>{name}</span>
                  </div>
                  {ev.evaluator_marks !== undefined && ev.evaluator_marks !== null && (
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getScoreColor(Number(ev.evaluator_marks))}`}>
                      {ev.evaluator_marks}/100
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {ev.evaluator_feedback || 'No comments provided.'}
                </p>
                {ev.evaluated_at && (
                  <p className="text-xs text-gray-400 text-right">{new Date(ev.evaluated_at).toLocaleString()}</p>
                )}
              </div>
            )
          })}
        </div>
      )}

      {!loading && evaluations.length === 0 && !showForm && (
        <p className="text-xs text-gray-500">No evaluations yet.</p>
      )}
    </div>
  )
}
